import api from '@/services/axiosApi'

export interface NodeMetrics {
  nodeId: string
  nodeType: string
  visitorCount: number
  avgDurationMs: number
  splitDistribution?: Record<string, number>
  answerDistribution?: Record<string, number>
  branchDistribution?: Record<string, number>
  offerDistribution?: Record<string, number>
  bucketDistribution?: Record<string, number>
  conversionCount?: number
  conversionRate?: number
}

export interface FlowMetrics {
  totalExecutions: number
  uniqueVisitors: number
  identifiedVisitors: number
  avgDurationMs: number
  completionBreakdown: Record<string, number>
  byFlow: Array<{
    flowId: string
    executions: number
    uniqueVisitors: number
    completionRate: number
  }>
}

export interface PathFlow {
  from: string
  to: string
  count: number
}

export interface DropOff {
  nodeId: string
  count: number
}

export interface FlowExecutionMetricsResponse {
  flowMetrics: FlowMetrics
  nodeMetrics: Record<string, NodeMetrics>
  pathFlows?: {
    paths: PathFlow[]
    dropOffs: DropOff[]
  }
}

export const fetchFlowExecutionMetrics = async (
  teamPid: string,
  params: {
    flowId?: string
    start: Date
    end: Date
    timezone?: string
  }
): Promise<FlowExecutionMetricsResponse> => {
  const { data } = await api.get<FlowExecutionMetricsResponse>(
    `/api/${teamPid}/metrics/flowExecutionMetrics`,
    {
      params: {
        flowId: params.flowId,
        start: params.start.toISOString(),
        end: params.end.toISOString(),
        timezone: params.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
      }
    }
  )
  
  return data
}

export interface BatchFlowExecutionMetricsResponse {
  flows: Record<string, FlowExecutionMetricsResponse>
}

export const fetchBatchFlowExecutionMetrics = async (
  teamPid: string,
  params: {
    flowIds: string[]
    start: Date
    end: Date
    timezone?: string
  }
): Promise<BatchFlowExecutionMetricsResponse> => {
  const { data } = await api.get<BatchFlowExecutionMetricsResponse>(
    `/api/${teamPid}/metrics/flowExecutionMetrics`,
    {
      params: {
        flowIds: params.flowIds,
        start: params.start.toISOString(),
        end: params.end.toISOString(),
        timezone: params.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
      }
    }
  )
  
  return data
}

export interface FlowSummaryMetrics {
  flowId: string
  executions: number
  uniqueVisitors: number
  identifiedVisitors: number
  completionRate: number
  avgDurationMs: number
}

export interface FlowSummaryResponse {
  flows: Record<string, FlowSummaryMetrics>
}

export const fetchFlowSummaryMetrics = async (
  teamPid: string,
  params: {
    flowIds: string[]
    start: Date
    end: Date
    timezone?: string
  }
): Promise<FlowSummaryResponse> => {
  // Use POST if we have many flow IDs to avoid URL length limits
  // Typically URLs should stay under 2000 characters
  const usePost = params.flowIds.length > 20
  
  const requestData = {
    flowIds: params.flowIds,
    start: params.start.toISOString(),
    end: params.end.toISOString(),
    timezone: params.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
  }
  
  const { data } = usePost
    ? await api.post<FlowSummaryResponse>(
        `/api/${teamPid}/metrics/flowSummaryMetrics`,
        requestData
      )
    : await api.get<FlowSummaryResponse>(
        `/api/${teamPid}/metrics/flowSummaryMetrics`,
        { params: requestData }
      )
  
  return data
}

export const deleteFlowExecutions = async (
  teamPid: string,
  flowId?: string
): Promise<{ success: boolean; deleted: number; flowId?: string }> => {
  const response = await api.delete(`/api/${teamPid}/metrics/flowExecutions`, {
    data: { flowId }
  })
  return response.data
}

export interface FlowExecutionNode {
  nodeId: string
  nodeType: string
  timestamp?: string
  durationFromPrevious?: number
  offerId?: string
  segmentGroupId?: string
  action?: string
  branch?: string
  splitResult?: string
  url?: string
  conversionType?: string
  selectedSegmentId?: string
}

export interface RecentFlowVisitor {
  visitorId: string
  emailAddress: string | null
  completionStatus: string
  timestamp: string
  durationMs: number
  pageUrl: string | null
  lastNode: FlowExecutionNode | null
}

export interface RecentFlowVisitorsResponse {
  visitors: RecentFlowVisitor[]
  total: number
}

// --- Flow Engagement Types & API ---

export interface FlowEngagementNode {
  nodeId: string
  nodeType: string
  timestamp?: string
  durationFromPrevious?: number
  offerId?: string
  segmentGroupId?: string
  action?: string
  branch?: string
  splitResult?: string
  url?: string
  conversionType?: string
  selectedSegmentId?: string | string[]
  freeformInput?: string
  otherText?: string
  inputValue?: string
  inputType?: string
  questionId?: string
}

export interface FlowEngagement {
  timestamp: string
  email: string
  nodes: FlowEngagementNode[]
  completionStatus: string
  durationMs: number
  pageUrl: string | null
  deviceType: string | null
  country: string | null
}

export interface FlowEngagementsResponse {
  engagements: FlowEngagement[]
  total: number
  totalAll: number
  searchAfter: string | null
}

export const fetchFlowEngagements = async (
  teamPid: string,
  params: {
    flowId: string
    start: Date
    end: Date
    email?: string
    limit?: number
    searchAfter?: string
    timezone?: string
  }
): Promise<FlowEngagementsResponse> => {
  const { data } = await api.get<FlowEngagementsResponse>(
    `/api/${teamPid}/metrics/flowEngagements`,
    {
      params: {
        flowId: params.flowId,
        start: params.start.toISOString(),
        end: params.end.toISOString(),
        email: params.email || undefined,
        limit: params.limit || 50,
        searchAfter: params.searchAfter || undefined,
        timezone: params.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
      }
    }
  )
  return data
}

export const fetchRecentFlowVisitors = async (
  teamPid: string,
  params: {
    flowId: string
    start: Date
    end: Date
    limit?: number
    identifiedOnly?: boolean
  }
): Promise<RecentFlowVisitorsResponse> => {
  const { data } = await api.get<RecentFlowVisitorsResponse>(
    `/api/${teamPid}/metrics/recentFlowVisitors`,
    {
      params: {
        flowId: params.flowId,
        start: params.start.toISOString(),
        end: params.end.toISOString(),
        limit: params.limit || 50,
        identifiedOnly: params.identifiedOnly ? 1 : 0
      }
    }
  )

  return data
}